import { FC, useEffect } from 'react';
import { Button, Space, message } from 'antd';
import { useTranslation } from 'react-i18next';
import type { ActionsProps } from './Actions.types';
import { APP_SCOPE, NAMESPACE, DELETE_ACTION_COLOR } from '@/constants';
import { useRole } from '@/hooks/useRole';
import { useDeleteRateCardMutation } from '@/api';
import { useConfirmModal } from '@/hooks/useConfirmModal';

const Actions: FC<ActionsProps> = ({
  editRateCard,
  saveRateCard,
  cancel,
  id,
  isNewRateCard,
  isEditing,
  isLoading
}) => {
  const { t } = useTranslation(NAMESPACE.RATE_CARDS_PAGE);
  const { canDelete, canUpdate } = useRole(APP_SCOPE.RATE_CARD);
  const [deleteRateCard, { isSuccess: isDeletionSuccessful, isLoading: isRateCardDeleting }] =
    useDeleteRateCardMutation();
  const { handleConfirm: handleDeleteRateCard } = useConfirmModal({
    title: t('deleteRateCardModalTitle'),
    content: t('deleteRateCardModalDescription'),
    destructive: true,
    okText: t('cancel', { ns: NAMESPACE.COMMON }),
    cancelText: t('delete', { ns: NAMESPACE.COMMON }),
    onCancel: () => deleteRateCard(id)
  });

  useEffect(() => {
    if (isDeletionSuccessful) {
      message.success(t('rateCardHasBeenDeleted'));
    }
  }, [isDeletionSuccessful, t]);

  return (
    <Space size={0}>
      {!isEditing && !isNewRateCard ? (
        <>
          {canUpdate && (
            <Button onClick={editRateCard} type="link">
              {t('edit', { ns: NAMESPACE.COMMON })}
            </Button>
          )}
          {canDelete && (
            <Button
              disabled={isRateCardDeleting}
              onClick={handleDeleteRateCard}
              type="link"
              danger
              style={{ color: DELETE_ACTION_COLOR }}
            >
              {t('delete', { ns: NAMESPACE.COMMON })}
            </Button>
          )}
        </>
      ) : (
        <>
          <Button disabled={isLoading} onClick={() => saveRateCard(id)} type="link">
            {t('save', { ns: NAMESPACE.COMMON })}
          </Button>
          <Button onClick={cancel} danger type="link">
            {t('cancel', { ns: NAMESPACE.COMMON })}
          </Button>
        </>
      )}
    </Space>
  );
};

export default Actions;
